import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageProps } from '../types/auth';
import styles from './styles/Report.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Group {
  group_id: number;
  name: string;
  curriculum_id?: number;
}

interface Curriculum {
  curriculum_id: number;
  name: string;
}

const GroupAttendanceReport: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<Group[]>([]);
  const [curriculums, setCurriculums] = useState<Curriculum[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [selectedCurriculum, setSelectedCurriculum] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [error, setError] = useState<string>('');

  // Set default dates (from: 30 days ago, to: today)
  useEffect(() => {
    const today = new Date();
    const defaultFromDate = new Date();
    defaultFromDate.setDate(today.getDate() - 7);
    
    setToDate(today.toISOString().split('T')[0]);
    setFromDate(defaultFromDate.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/groups`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Org-ID': selectedOrgID?.toString() || '',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch groups');
        }

        const data: Group[] = await response.json();
        setGroups(data);
      } catch (err) {
        setError('Failed to load groups');
      }
    };

    const fetchCurriculums = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/curriculums`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Org-ID': selectedOrgID?.toString() || '',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch curriculums');
        }

        const data: Curriculum[] = await response.json();
        setCurriculums(data);
      } catch (err) {
        setError('Failed to load curriculums');
      }
    };

    fetchGroups();
    fetchCurriculums();
  }, [token, selectedOrgID]);

  useEffect(() => {
    if (selectedGroup) {
      const group = groups.find(g => g.group_id.toString() === selectedGroup);
      if (group?.curriculum_id) {
        setSelectedCurriculum(group.curriculum_id.toString());
      }
    }
  }, [selectedGroup, groups]);

  const handleSubmit = async () => {
    if (!selectedGroup || !selectedCurriculum || !fromDate || !toDate) {
      setError('Please fill in all fields');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/reports/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Org-ID': selectedOrgID?.toString() || '',
        },
        body: JSON.stringify({
          report_name: 'group_attendance',
          parameters: {
            group_id: parseInt(selectedGroup, 10),
            curriculum_id: parseInt(selectedCurriculum, 10),
            from_date: fromDate,
            to_date: toDate
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to start report');
      }

      navigate('/reports');
    } catch (err) {
      setError('Failed to start report');
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Group Attendance Report</h1>
      
      {error && <p className={styles.error}>{error}</p>}

      <div className={styles.formGroup}>
        <label className={styles.label}>Group:</label>
        <select
          value={selectedGroup}
          onChange={(e) => setSelectedGroup(e.target.value)}
          className={styles.select}
        >
          <option value="">Select a group</option>
          {groups.map((group) => (
            <option key={group.group_id} value={group.group_id}>
              {group.name}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Curriculum:</label>
        <select
          value={selectedCurriculum}
          onChange={(e) => setSelectedCurriculum(e.target.value)}
          className={styles.select}
        >
          <option value="">Select a curriculum</option>
          {curriculums.map((curriculum) => (
            <option key={curriculum.curriculum_id} value={curriculum.curriculum_id}>
              {curriculum.name}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>From Date:</label>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className={styles.input}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>To Date:</label>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className={styles.input}
        />
      </div>

      <button
        onClick={handleSubmit}
        className={styles.submitButton}
        disabled={!selectedGroup || !selectedCurriculum || !fromDate || !toDate}
      >
        Run Report
      </button>
    </div>
  );
};

export default GroupAttendanceReport; 